// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import { useState } from 'react';

import { useAdminToken } from './admin-token';

// Shown when a locked-org page gets a 401 -- there is no in-app refresh
// flow (see marketplace-welcome/root.tsx's identical copy for the initial
// link step); re-entry is via AWS Marketplace's own idempotent
// registration redirect, which always mints a fresh session token.
export const SESSION_EXPIRED_MESSAGE =
  'Your session has expired -- please return to your AWS Marketplace subscription ("Manage subscription" / "Set up your account") to continue.';

export function readUrlParam(name: string): string | null {
  return new URLSearchParams(window.location.search).get(name);
}

// Present when a page was reached via the Marketplace welcome page's
// redirect (or a bookmarked ...html?org=... link) -- once set, the org is
// authoritative (see deployments/root.tsx's original use of this pattern)
// so a customer can't submit under an org unrelated to what Marketplace is
// actually billing. Without it, free-text/internal behavior is unchanged.
export function readLockedOrgFromUrl(): string | null {
  return readUrlParam('org');
}

// The session token proving this browser is the one Marketplace actually
// redirected here for this org. Persisted per-org so a reload of this exact
// page doesn't lose it even without the token still in the URL.
export function tokenStorageKey(org: string): string {
  return `ncoda.marketplaceSessionToken:${org}`;
}

export function readLockedTokenFromUrl(org: string | null): string | null {
  const fromUrl = readUrlParam('token');
  if (!org) {
    return fromUrl;
  }
  if (fromUrl) {
    window.localStorage.setItem(tokenStorageKey(org), fromUrl);
    return fromUrl;
  }
  return window.localStorage.getItem(tokenStorageKey(org));
}

export function useLockedIdentity() {
  const [org] = useState<string | null>(readLockedOrgFromUrl);
  const [token] = useState<string | null>(() => readLockedTokenFromUrl(org));
  // Only a genuinely resolvable token (from the URL, or a prior visit's
  // localStorage entry -- see readLockedTokenFromUrl) proves this is a
  // real Marketplace-redirected session. A bare ?org= with nothing backing
  // it (e.g. a hand-typed/bookmarked link, or an internal my-apps.html
  // link that predates the orgName= convention) must NOT lock the page --
  // confirmed live 2026-08-04: it hid the AdminTokenField, disabled the
  // org input, and used a null lockedToken for every API call, with no
  // way to recover short of editing the URL. Treating lockedOrg as unset
  // whenever there's no real token lets every downstream usage (auth,
  // "locked" UI copy, disabled fields) fall through to the ordinary
  // internal/free-text path uniformly, in one place, instead of needing
  // every page to separately guard against a tokenless org param.
  const lockedOrg = token ? org : null;
  return { lockedOrg, lockedToken: token };
}

// Whichever credential this session actually has: a Marketplace session
// token for a locked (Marketplace-linked) org, or the internal admin token
// for the free-text/internal path -- see checkOrgAccess on the backend.
// Side-nav links are plain hrefs to sibling .html pages -- without this,
// following one from a locked-org page drops straight back to the
// unauthenticated/free-text path on the next page (no org, no token), even
// though the customer never left their own session.
export function withLockedParams(href: string, lockedOrg: string | null, lockedToken: string | null): string {
  if (!lockedOrg || href.startsWith('#')) {
    return href;
  }
  const params = new URLSearchParams({ org: lockedOrg });
  if (lockedToken) {
    params.set('token', lockedToken);
  }
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}${params.toString()}`;
}

export function useAuthToken() {
  const { lockedOrg, lockedToken } = useLockedIdentity();
  const [adminToken, setAdminToken] = useAdminToken();
  const authToken = lockedOrg ? lockedToken : adminToken || null;
  return { lockedOrg, lockedToken, adminToken, setAdminToken, authToken };
}
